import { useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toPng } from "html-to-image";

import { Icon } from "@majourneys/icons";
export const Route = createFileRoute("/agafay-tarifs")({
  component: AgafayTarifsPage,
});

// ── i18n ────────────────────────────────────────────────────────────────────
type Lang = "fr" | "en";
type Loc = { fr: string; en: string };
const tr = (l: Loc, lang: Lang) => l[lang];

const UI = {
  fr: {
    ratesTitle: "Tarifs des camps",
    sub: "Daypass & nightpass",
    desertTitle: "Nos tarifs",
    desertLead: "Piscine, dîner & spectacle au cœur du désert. Prix par personne, en dirhams.",
    compareTitle: "Comparatif des tarifs",
    compareSub: "Par personne · prix en MAD · paiement sur place",
    show: "Spectacle",
    activities: "Activités",
    transport: "Transport non inclus",
    book: "Réservation via WhatsApp ou www.majourneys.com",
    payOnsite: "Paiement sur place",
    download: "Télécharger en PNG",
    downloading: "Génération…",
  },
  en: {
    ratesTitle: "Camp rates",
    sub: "Daypass & nightpass",
    desertTitle: "Our rates",
    desertLead: "Pool, dinner & live show in the heart of the desert. Price per person, in dirhams.",
    compareTitle: "Rate comparison",
    compareSub: "Per person · prices in MAD · pay on arrival",
    show: "Show",
    activities: "Activities",
    transport: "Transport not included",
    book: "Book via WhatsApp or www.majourneys.com",
    payOnsite: "Pay on arrival",
    download: "Download as PNG",
    downloading: "Rendering…",
  },
} as const;

// ── Data ─────────────────────────────────────────────────────────────────────
const CF = "https://imagedelivery.net/9h9OrUsEhPYeYHCr6DaDTQ";

type Pkg = { name: Loc; price: number; time: Loc };
type ActivityItem = { name: Loc; price: number; duration: string };
type Camp = {
  name: string;
  accent: string;
  image: string;
  packages: Pkg[];
  show: Loc[];
  activities: ActivityItem[];
};

const ACT = {
  quad: { fr: "Quad", en: "Quad bike" },
  camel: { fr: "Chameau", en: "Camel ride" },
} satisfies Record<string, Loc>;

const PKG = {
  poolLunch: { fr: "Piscine + Déjeuner", en: "Pool + Lunch" },
  poolDinner: { fr: "Piscine + Dîner + spectacle", en: "Pool + Dinner + show" },
  dinner: { fr: "Dîner + spectacle", en: "Dinner + show" },
  fullDay: { fr: "Journée Complète", en: "Full Day" },
} satisfies Record<string, Loc>;

const SHOW = {
  gnaoua: { fr: "Troupe Gnaoua", en: "Gnaoua troupe" },
  dancer: { fr: "Danseuse orientale", en: "Oriental dancer" },
  fire: { fr: "Spectacle de feu", en: "Fire show" },
  dj: { fr: "Musique de fond & DJ", en: "Background music & DJ" },
} satisfies Record<string, Loc>;

const CAMPS: Camp[] = [
  {
    name: "Le Bédouin",
    accent: "#c8643c",
    image: `${CF}/5c926170-ff92-4f34-eda7-99e573dd0d00/public`,
    packages: [
      { name: PKG.poolLunch, price: 450, time: { fr: "11h – 18h", en: "11am – 6pm" } },
      { name: PKG.poolDinner, price: 550, time: { fr: "15h – 23h", en: "3pm – 11pm" } },
      { name: PKG.dinner, price: 550, time: { fr: "19h – 23h", en: "7pm – 11pm" } },
      { name: PKG.fullDay, price: 1000, time: { fr: "11h – 23h", en: "11am – 11pm" } },
    ],
    show: [SHOW.gnaoua, SHOW.dancer, SHOW.fire, SHOW.dj],
    activities: [
      { name: ACT.quad, price: 500, duration: "1h" },
      { name: ACT.camel, price: 300, duration: "1h" },
    ],
  },
  {
    name: "Alkamar Camp",
    accent: "#b88a3b",
    image: `${CF}/64738288-ad96-4be7-99d7-a6cc893b6100/public`,
    packages: [
      { name: PKG.poolLunch, price: 450, time: { fr: "11h – 17h", en: "11am – 5pm" } },
      { name: PKG.poolDinner, price: 450, time: { fr: "17h – 23h", en: "5pm – 11pm" } },
      { name: PKG.dinner, price: 350, time: { fr: "19h – 23h", en: "7pm – 11pm" } },
      { name: PKG.fullDay, price: 800, time: { fr: "11h – 23h", en: "11am – 11pm" } },
    ],
    show: [SHOW.gnaoua, SHOW.fire],
    activities: [
      { name: ACT.quad, price: 300, duration: "1h" },
      { name: ACT.camel, price: 250, duration: "1h" },
    ],
  },
];

const Img = (props: React.ImgHTMLAttributes<HTMLImageElement>) => (
  // crossOrigin so html-to-image can export the CDN photos without tainting the canvas.
  // No loading="lazy": a cloned lazy image never fires `load`, which hangs toPng().
  <img crossOrigin="anonymous" {...props} />
);

// Fixed export width (px) per card → deterministic, WhatsApp-friendly PNGs that
// don't depend on the browser window size. Captured at 2× for crisp sharing.
const EXPORT_PIXEL_RATIO = 2;

// html-to-image's default web-font scan walks every stylesheet on the page,
// including cross-origin ones injected by browser extensions, and rejects when
// it can't read them. We pre-collect only our own (same-origin) @font-face rules
// and hand them over via `fontEmbedCSS`, which skips that fragile scan entirely.
// Load a (CORS-enabled) image through the <img> pipeline — which content/privacy
// extensions allow — and rasterise it to a data URL via <canvas>. html-to-image's
// own fetch() of CDN images is often blocked by those extensions ("Failed to
// fetch"); feeding it data URLs sidesteps the network entirely.
function urlToDataUrl(src: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = im.naturalWidth;
        c.height = im.naturalHeight;
        const ctx = c.getContext("2d");
        if (!ctx) return reject(new Error("no 2d context"));
        ctx.drawImage(im, 0, 0);
        resolve(c.toDataURL("image/jpeg", 0.92));
      } catch (e) {
        reject(e);
      }
    };
    im.onerror = () => reject(new Error(`image load failed: ${src}`));
    im.src = src;
  });
}

// Temporarily swap every <img> in `node` to an inlined data URL, run `fn`, then
// restore the original src values.
async function withInlinedImages<T>(node: HTMLElement, fn: () => Promise<T>): Promise<T> {
  const imgs = Array.from(node.querySelectorAll("img"));
  const originals = imgs.map((img) => img.getAttribute("src"));
  await Promise.all(
    imgs.map(async (img, i) => {
      const src = originals[i];
      if (!src || src.startsWith("data:")) return;
      try {
        img.setAttribute("src", await urlToDataUrl(src));
      } catch (e) {
        console.warn("inline image failed, leaving original", src, e);
      }
    }),
  );
  try {
    return await fn();
  } finally {
    imgs.forEach((img, i) => {
      const src = originals[i];
      if (src != null) img.setAttribute("src", src);
      else img.removeAttribute("src");
    });
  }
}

let fontEmbedCache: string | null = null;
function collectFontEmbedCSS(): string {
  if (fontEmbedCache != null) return fontEmbedCache;
  const parts: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList | null = null;
    try {
      rules = sheet.cssRules;
    } catch {
      rules = null; // cross-origin / extension sheet — skip
    }
    if (!rules) continue;
    for (const rule of Array.from(rules)) {
      if (rule.type === CSSRule.FONT_FACE_RULE) parts.push(rule.cssText);
    }
  }
  fontEmbedCache = parts.join("\n");
  return fontEmbedCache;
}

// ── Page ─────────────────────────────────────────────────────────────────────
function AgafayTarifsPage() {
  const [lang, setLang] = useState<Lang>("fr");

  return (
    <div className="min-h-screen bg-[#f4f3f9] pb-28">
      <div className="mx-auto max-w-[920px] px-4 pt-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#7a7d99]">
              Agafay · cartes de tarifs · /agafay-tarifs
            </h2>
            <h1
              className="mb-3 text-[28px] font-bold leading-[1.05] text-[#110E39]"
              style={{ fontFamily: "var(--font-geist)" }}
            >
              {lang === "fr" ? "Tarifs Agafay · prêts à télécharger" : "Agafay rates · ready to download"}
            </h1>
            <p className="max-w-[640px] text-[13px] leading-relaxed text-[#5a5d77]">
              {lang === "fr"
                ? "Choisis une carte, clique « Télécharger en PNG », et envoie l'image au client. Bascule FR / EN selon le client."
                : "Pick a card, click “Download as PNG”, and send the image to the client. Switch FR / EN to match the client."}
            </p>
          </div>
          <LangToggle lang={lang} setLang={setLang} />
        </div>
      </div>

      <div className="mx-auto mt-10 max-w-[920px] space-y-16 px-4">
        <ConceptBlock
          n={2}
          title={lang === "fr" ? "Nuit étoilée · premium" : "Starlit night · premium"}
          desc={
            lang === "fr"
              ? "Fond nuit du désert, accents or, allure carte de tarifs d'un lodge haut de gamme."
              : "Desert-night background, gold accents, the rate-card look of a high-end lodge."
          }
        >
          <Exportable filename={`agafay-rates-night-${lang}`} width={680} lang={lang}>
            <NightCard lang={lang} />
          </Exportable>
        </ConceptBlock>

        <ConceptBlock
          n={4}
          title={lang === "fr" ? "Nuit étoilée · photo immersive" : "Starlit night · photo-forward"}
          desc={
            lang === "fr"
              ? "La photo de chaque camp prend le haut de la carte avec le nom en surimpression."
              : "Each camp's photo headlines the card with its name overlaid."
          }
        >
          <Exportable filename={`agafay-rates-photo-${lang}`} width={720} lang={lang}>
            <NightPhotoCard lang={lang} />
          </Exportable>
        </ConceptBlock>

        <ConceptBlock
          n={5}
          title={lang === "fr" ? "Nuit étoilée · bandeaux horizontaux" : "Starlit night · horizontal bands"}
          desc={
            lang === "fr"
              ? "Chaque camp en bandeau large : grande photo d'un côté, formules en grille de l'autre."
              : "Each camp as a wide band: large photo on one side, packages in a grid on the other."
          }
        >
          <Exportable filename={`agafay-rates-bands-${lang}`} width={780} lang={lang}>
            <NightBandCard lang={lang} />
          </Exportable>
        </ConceptBlock>

        <ConceptBlock
          n={6}
          title={lang === "fr" ? "Nuit étoilée · photo plein cadre + verre" : "Starlit night · full-bleed + glass"}
          desc={
            lang === "fr"
              ? "La photo occupe toute la carte ; le tarif flotte dessus dans un panneau en verre dépoli."
              : "The photo fills the whole card; the rates float over it in a frosted-glass panel."
          }
        >
          <Exportable filename={`agafay-rates-glass-${lang}`} width={720} lang={lang}>
            <NightGlassCard lang={lang} />
          </Exportable>
        </ConceptBlock>

        <ConceptBlock
          n={7}
          title={lang === "fr" ? "Nuit étoilée · billet premium" : "Starlit night · premium ticket"}
          desc={
            lang === "fr"
              ? "Style billet : bande photo verticale à gauche, perforation pointillée, formules à droite."
              : "Ticket style: vertical photo stub on the left, dashed perforation, packages on the right."
          }
        >
          <Exportable filename={`agafay-rates-ticket-${lang}`} width={780} lang={lang}>
            <NightTicketCard lang={lang} />
          </Exportable>
        </ConceptBlock>

        <ConceptBlock
          n={1}
          title={lang === "fr" ? "Carte désert · chaleureux" : "Desert card · warm"}
          desc={
            lang === "fr"
              ? "Sable & terracotta, titres en serif, prix en pastilles, spectacle en tags."
              : "Sand & terracotta, serif headlines, prices as pills, show as tags."
          }
        >
          <Exportable filename={`agafay-rates-desert-${lang}`} width={680} lang={lang}>
            <DesertCard lang={lang} />
          </Exportable>
        </ConceptBlock>

        <ConceptBlock
          n={3}
          title={lang === "fr" ? "Comparatif côte à côte" : "Side-by-side comparison"}
          desc={
            lang === "fr"
              ? "Les deux camps en colonnes, les formules en lignes — le moins cher mis en avant."
              : "Both camps as columns, packages as rows — the cheaper price highlighted."
          }
        >
          <Exportable filename={`agafay-rates-compare-${lang}`} width={760} lang={lang}>
            <CompareCard lang={lang} />
          </Exportable>
        </ConceptBlock>
      </div>
    </div>
  );
}

function LangToggle({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  return (
    <div className="flex shrink-0 overflow-hidden rounded-full border border-[#d8d6e6] bg-white p-0.5 shadow-sm">
      {(["fr", "en"] as const).map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLang(l)}
          className={`rounded-full px-4 py-1.5 text-[12px] font-bold uppercase tracking-[0.12em] transition ${
            lang === l ? "bg-[#110E39] navy-depth text-white" : "text-[#5a5d77] hover:text-[#110E39]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function ConceptBlock({
  n,
  title,
  desc,
  children,
}: {
  n: number;
  title: string;
  desc: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-4">
        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#7a7d99]">
          Concept {n}
        </span>
        <h3
          className="text-[20px] font-bold leading-tight text-[#110E39]"
          style={{ fontFamily: "var(--font-geist)" }}
        >
          {title}
        </h3>
        <p className="mt-1 max-w-[620px] text-[13px] leading-relaxed text-[#5a5d77]">{desc}</p>
      </div>
      {children}
    </div>
  );
}

// ── Download wrapper ─────────────────────────────────────────────────────────
function Exportable({
  filename,
  width,
  lang,
  children,
}: {
  filename: string;
  width: number;
  lang: Lang;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);

  async function download() {
    const node = ref.current;
    if (!node || busy) return;
    setBusy(true);
    const prevWidth = node.style.width;
    try {
      // Pin a fixed width so the exported PNG is the same size regardless of the
      // browser window, then measure the height that content reflows to.
      node.style.width = `${width}px`;
      void node.getBoundingClientRect(); // force reflow
      const height = node.scrollHeight;

      const opts = {
        width,
        height,
        pixelRatio: EXPORT_PIXEL_RATIO,
        cacheBust: false,
        fontEmbedCSS: collectFontEmbedCSS(),
      };

      const url = await withInlinedImages(node, async () => {
        // First pass primes the SVG/font cache; the second pass renders fully.
        await toPng(node, opts);
        return toPng(node, opts);
      });

      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error("PNG export failed", err);
      // eslint-disable-next-line no-alert
      window.alert("Export PNG impossible — réessaie. (détails dans la console)");
    } finally {
      node.style.width = prevWidth;
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto" style={{ maxWidth: width }}>
      <div ref={ref}>{children}</div>
      <div className="mt-3 flex justify-center">
        <button
          type="button"
          onClick={download}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-[#110E39] navy-depth px-5 py-2 text-[12px] font-bold uppercase tracking-[0.12em] text-white transition hover:bg-[#2a2660] disabled:opacity-60"
          style={{ fontFamily: "var(--font-geist)" }}
        >
          <Icon name={busy ? "ri-loader-4-line animate-spin text-[14px]" : "ri-download-2-line text-[14px]"} aria-hidden="true" />
          {busy ? UI[lang].downloading : UI[lang].download}
        </button>
      </div>
    </div>
  );
}

// ── Concept 1 · Desert card ─────────────────────────────────────────────────
function DesertCard({ lang }: { lang: Lang }) {
  const t = UI[lang];
  return (
    <div
      className="relative w-full overflow-hidden rounded-[20px] shadow-[0_30px_70px_rgba(120,70,30,0.30)]"
      style={{ background: "linear-gradient(165deg, #f6e7cf 0%, #f0d3ad 40%, #e7b98a 100%)" }}
    >
      <div
        aria-hidden
        className="absolute -right-16 -top-16 h-56 w-56 rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,225,170,0.95) 0%, rgba(240,180,110,0.5) 45%, transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-24"
        style={{
          background:
            "radial-gradient(120% 100% at 30% 100%, rgba(180,110,60,0.30) 0%, transparent 60%), radial-gradient(120% 100% at 80% 100%, rgba(150,90,50,0.25) 0%, transparent 55%)",
        }}
      />

      <div className="relative px-7 py-8 sm:px-10 sm:py-10">
        <div className="flex items-center justify-between">
          <span
            className="text-[11px] font-bold uppercase tracking-[0.32em] text-[#8a4a22]"
            style={{ fontFamily: "var(--font-geist)" }}
          >
            MaJourneys
          </span>
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-[#8a4a22]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#8a4a22]"
            style={{ fontFamily: "var(--font-geist)" }}
          >
            <Icon name="ri-sun-line" className="text-[12px]" aria-hidden="true" />
            Agafay
          </span>
        </div>

        <h2
          className="mt-4 text-[34px] font-bold leading-[0.98] text-[#5e3014] sm:text-[42px]"
          style={{ fontFamily: "Georgia, ui-serif, serif" }}
        >
          {t.desertTitle}
          <span className="italic"> Agafay</span>
        </h2>
        <p className="mt-1 text-[12px] font-bold uppercase tracking-[0.22em] text-[#9a6a40]">
          {t.sub}
        </p>
        <p className="mt-1.5 text-[13px] leading-relaxed text-[#7a4a26]">{t.desertLead}</p>

        <div className="mt-7 space-y-6">
          {CAMPS.map((camp) => (
            <div key={camp.name}>
              <div className="flex items-center gap-3">
                <span
                  className="h-12 w-12 shrink-0 overflow-hidden rounded-[10px] shadow-sm ring-2 ring-white/70"
                  aria-hidden="true"
                >
                  <Img src={camp.image} alt="" className="h-full w-full object-cover" />
                </span>
                <h3
                  className="text-[19px] font-bold text-[#5e3014]"
                  style={{ fontFamily: "var(--font-geist)" }}
                >
                  {camp.name}
                </h3>
                <span className="h-px flex-1" style={{ background: `${camp.accent}55` }} />
              </div>

              <ul className="mt-3 space-y-2">
                {camp.packages.map((p) => (
                  <li
                    key={p.name.en}
                    className="flex items-center gap-3 rounded-[12px] bg-white/55 px-3.5 py-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="block break-words text-[14px] font-bold text-[#4a2810]">
                        {tr(p.name, lang)}
                      </span>
                      <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-[#9a6a40]">
                        {tr(p.time, lang)}
                      </span>
                    </div>
                    <span
                      className="shrink-0 rounded-full px-3 py-1 text-[13px] font-bold tabular-nums text-white"
                      style={{ background: camp.accent }}
                    >
                      {p.price} MAD
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#9a6a40]">
                  {t.show} :
                </span>
                {camp.show.map((s) => (
                  <span
                    key={s.en}
                    className="rounded-full border border-[#8a4a22]/25 px-2.5 py-0.5 text-[11px] font-medium text-[#7a4a26]"
                  >
                    {tr(s, lang)}
                  </span>
                ))}
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#9a6a40]">
                  {t.activities} :
                </span>
                {camp.activities.map((a) => (
                  <span
                    key={a.name.en}
                    className="inline-flex items-center gap-1 rounded-full bg-[#8a4a22]/10 px-2.5 py-0.5 text-[11px] font-medium text-[#7a4a26]"
                  >
                    {tr(a.name, lang)}
                    <span className="font-bold tabular-nums text-[#8a4a22]">{a.price} MAD</span>
                    <span className="text-[#9a6a40]">· {a.duration}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center gap-2 rounded-[12px] bg-[#8a4a22]/10 px-3.5 py-2.5">
          <Icon name="ri-car-line" className="text-[15px] text-[#8a4a22]" aria-hidden="true" />
          <span className="text-[12px] font-semibold text-[#7a4a26]">{t.transport}</span>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-[#8a4a22]/20 pt-4">
          <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-[#9a6a40]">
            {t.payOnsite}
          </span>
          <span
            className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[#1f7a4d]"
            style={{ fontFamily: "var(--font-geist)" }}
          >
            <Icon name="ri-whatsapp-line" className="text-[15px]" aria-hidden="true" />
            {t.book}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Concept 2 · Night premium card ──────────────────────────────────────────
function NightCard({ lang }: { lang: Lang }) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-[20px] shadow-[0_30px_80px_rgba(8,10,30,0.55)]"
      style={{ background: NIGHT_BG }}
    >
      <Stars />
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-28"
        style={{
          background: "radial-gradient(80% 100% at 50% 100%, rgba(212,170,90,0.22) 0%, transparent 65%)",
        }}
      />
      <div className="relative px-7 py-9 sm:px-11 sm:py-11">
        <NightHeader lang={lang} />

        <div className="mt-8 grid grid-cols-1 gap-7 sm:grid-cols-2">
          {CAMPS.map((camp) => (
            <div
              key={camp.name}
              className="overflow-hidden rounded-[16px] border border-white/10 bg-white/[0.04]"
            >
              <div className="relative h-28 w-full overflow-hidden">
                <Img src={camp.image} alt={camp.name} className="h-full w-full object-cover" />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{ background: "linear-gradient(180deg, rgba(7,6,15,0.1) 0%, rgba(7,6,15,0.85) 100%)" }}
                />
                <h3
                  className="absolute inset-x-0 bottom-2 text-center text-[18px] font-bold uppercase tracking-[0.1em]"
                  style={{ fontFamily: "var(--font-geist)", color: "#f4ecd8" }}
                >
                  {camp.name}
                </h3>
              </div>
              <div className="p-5 pt-4">
                <ul className="space-y-3">
                  {camp.packages.map((p) => (
                    <NightPriceRow key={p.name.en} p={p} lang={lang} />
                  ))}
                </ul>
                <NightExtras camp={camp} lang={lang} />
              </div>
            </div>
          ))}
        </div>

        <NightFooter lang={lang} />
      </div>
    </div>
  );
}

// ── Shared night-theme bits ──────────────────────────────────────────────────
const NIGHT_BG = "radial-gradient(ellipse at 50% -10%, #2a2150 0%, #141235 45%, #07060f 100%)";

function NightHeader({ lang }: { lang: Lang }) {
  const t = UI[lang];
  return (
    <div className="text-center">
      <span
        className="text-[11px] font-bold uppercase tracking-[0.4em] text-[#d4aa5a]"
        style={{ fontFamily: "var(--font-geist)" }}
      >
        MaJourneys · Agafay
      </span>
      <div className="mx-auto mt-3 flex items-center justify-center gap-3">
        <span className="h-px w-10 bg-[#d4aa5a]/40" />
        <Icon name="ri-moon-clear-line" className="text-[16px] text-[#d4aa5a]" aria-hidden="true" />
        <span className="h-px w-10 bg-[#d4aa5a]/40" />
      </div>
      <h2
        className="mt-3 text-[32px] font-bold leading-tight text-[#f4ecd8] sm:text-[40px]"
        style={{ fontFamily: "Georgia, ui-serif, serif" }}
      >
        {t.ratesTitle}
      </h2>
      <p className="mt-1.5 text-[12px] uppercase tracking-[0.22em] text-white/55">{t.sub}</p>
    </div>
  );
}

function NightFooter({ lang }: { lang: Lang }) {
  const t = UI[lang];
  return (
    <>
      <div className="mt-7 flex items-center justify-center gap-2 rounded-full border border-[#d4aa5a]/30 bg-[#d4aa5a]/10 px-4 py-2">
        <Icon name="ri-car-line" className="text-[14px] text-[#d4aa5a]" aria-hidden="true" />
        <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#d4aa5a]">
          {t.transport}
        </span>
      </div>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[12px] font-medium text-white/75">
        <span className="inline-flex items-center gap-1.5">
          <Icon name="ri-whatsapp-line" className="text-[15px] text-[#3ec46a]" aria-hidden="true" />
          {t.book}
        </span>
      </div>
      <div className="mt-1 text-center text-[11px] uppercase tracking-[0.16em] text-white/45">
        {t.payOnsite}
      </div>
    </>
  );
}

function NightPriceRow({ p, lang }: { p: Pkg; lang: Lang }) {
  return (
    <li>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[13px] font-semibold text-[#f4ecd8]">{tr(p.name, lang)}</span>
        <span className="mx-1 hidden flex-1 border-b border-dotted border-white/20 sm:block" />
        <span
          className="shrink-0 text-[14px] font-bold tabular-nums"
          style={{ fontFamily: "var(--font-geist)", color: "#d4aa5a" }}
        >
          {p.price} MAD
        </span>
      </div>
      <span className="text-[10px] uppercase tracking-[0.14em] text-white/45">{tr(p.time, lang)}</span>
    </li>
  );
}

function NightActivities({ camp, lang }: { camp: Camp; lang: Lang }) {
  return (
    <div className="mt-3">
      <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#d4aa5a]/80">
        {UI[lang].activities}
      </span>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {camp.activities.map((a) => (
          <span
            key={a.name.en}
            className="inline-flex items-center gap-1 rounded-full border border-[#d4aa5a]/25 bg-white/[0.05] px-2.5 py-0.5 text-[11px] text-white/85"
          >
            {tr(a.name, lang)}
            <span className="font-bold tabular-nums text-[#d4aa5a]">{a.price} MAD</span>
            <span className="text-white/45">· {a.duration}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function NightExtras({ camp, lang }: { camp: Camp; lang: Lang }) {
  return (
    <div className="mt-4 border-t border-white/10 pt-3">
      <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#d4aa5a]/80">
        {UI[lang].show}
      </span>
      <p className="mt-1 text-[11px] leading-relaxed text-white/65">
        {camp.show.map((s) => tr(s, lang)).join(" · ")}
      </p>
      <NightActivities camp={camp} lang={lang} />
    </div>
  );
}

// ── Concept 4 · Night, photo-forward cards ──────────────────────────────────
function NightPhotoCard({ lang }: { lang: Lang }) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-[20px] shadow-[0_30px_80px_rgba(8,10,30,0.55)]"
      style={{ background: NIGHT_BG }}
    >
      <Stars />
      <div className="relative px-7 py-9 sm:px-10 sm:py-11">
        <NightHeader lang={lang} />

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {CAMPS.map((camp) => (
            <div
              key={camp.name}
              className="overflow-hidden rounded-[18px] border border-white/10 bg-white/[0.04] ring-1 ring-white/10"
            >
              <div className="relative h-[210px] w-full overflow-hidden">
                <Img src={camp.image} alt={camp.name} className="h-full w-full object-cover" />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(7,6,15,0) 30%, rgba(7,6,15,0.55) 65%, rgba(7,6,15,0.92) 100%)",
                  }}
                />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <span
                    className="inline-block rounded-full bg-[#d4aa5a]/20 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] text-[#d4aa5a]"
                    style={{ fontFamily: "var(--font-geist)" }}
                  >
                    Agafay
                  </span>
                  <h3
                    className="mt-1.5 text-[22px] font-bold uppercase leading-none tracking-[0.04em] text-[#f4ecd8]"
                    style={{ fontFamily: "var(--font-geist)" }}
                  >
                    {camp.name}
                  </h3>
                </div>
              </div>
              <div className="p-5">
                <ul className="space-y-3">
                  {camp.packages.map((p) => (
                    <NightPriceRow key={p.name.en} p={p} lang={lang} />
                  ))}
                </ul>
                <NightExtras camp={camp} lang={lang} />
              </div>
            </div>
          ))}
        </div>

        <NightFooter lang={lang} />
      </div>
    </div>
  );
}

// ── Concept 5 · Night, horizontal photo bands ───────────────────────────────
function NightBandCard({ lang }: { lang: Lang }) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-[20px] shadow-[0_30px_80px_rgba(8,10,30,0.55)]"
      style={{ background: NIGHT_BG }}
    >
      <Stars />
      <div className="relative px-7 py-9 sm:px-10 sm:py-11">
        <NightHeader lang={lang} />

        <div className="mt-8 space-y-5">
          {CAMPS.map((camp, i) => (
            <div
              key={camp.name}
              className="overflow-hidden rounded-[18px] border border-white/10 bg-white/[0.04] ring-1 ring-white/10"
            >
              <div className={`flex flex-col sm:flex-row ${i % 2 ? "sm:flex-row-reverse" : ""}`}>
                <div className="relative h-[200px] w-full shrink-0 overflow-hidden sm:h-auto sm:w-[44%]">
                  <Img
                    src={camp.image}
                    alt={camp.name}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{ background: "linear-gradient(180deg, rgba(7,6,15,0) 45%, rgba(7,6,15,0.85) 100%)" }}
                  />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <span
                      className="inline-block rounded-full bg-[#d4aa5a]/20 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] text-[#d4aa5a]"
                      style={{ fontFamily: "var(--font-geist)" }}
                    >
                      Agafay
                    </span>
                    <h3
                      className="mt-1.5 text-[22px] font-bold uppercase leading-none tracking-[0.04em] text-[#f4ecd8]"
                      style={{ fontFamily: "var(--font-geist)" }}
                    >
                      {camp.name}
                    </h3>
                  </div>
                </div>
                <div className="min-w-0 flex-1 p-5">
                  <ul className="grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2">
                    {camp.packages.map((p) => (
                      <NightPriceRow key={p.name.en} p={p} lang={lang} />
                    ))}
                  </ul>
                  <NightExtras camp={camp} lang={lang} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <NightFooter lang={lang} />
      </div>
    </div>
  );
}

// ── Concept 6 · Night, full-bleed photo + frosted glass panel ───────────────
function NightGlassCard({ lang }: { lang: Lang }) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-[20px] shadow-[0_30px_80px_rgba(8,10,30,0.55)]"
      style={{ background: NIGHT_BG }}
    >
      <Stars />
      <div className="relative px-7 py-9 sm:px-10 sm:py-11">
        <NightHeader lang={lang} />

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {CAMPS.map((camp) => (
            <div
              key={camp.name}
              className="relative h-[520px] overflow-hidden rounded-[18px] ring-1 ring-white/10"
            >
              <Img
                src={camp.image}
                alt={camp.name}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(7,6,15,0.55) 0%, rgba(7,6,15,0.15) 30%, rgba(7,6,15,0.55) 70%, rgba(7,6,15,0.85) 100%)",
                }}
              />
              <div className="absolute inset-0 flex flex-col justify-between p-4">
                <div>
                  <span
                    className="inline-block rounded-full bg-[#d4aa5a]/25 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] text-[#f4ecd8]"
                    style={{ fontFamily: "var(--font-geist)" }}
                  >
                    Agafay
                  </span>
                  <h3
                    className="mt-1.5 text-[24px] font-bold uppercase leading-none tracking-[0.04em] text-[#f4ecd8] drop-shadow"
                    style={{ fontFamily: "var(--font-geist)" }}
                  >
                    {camp.name}
                  </h3>
                </div>

                <div className="rounded-[14px] border border-white/15 bg-black/40 p-4 backdrop-blur-md">
                  <ul className="space-y-2.5">
                    {camp.packages.map((p) => (
                      <NightPriceRow key={p.name.en} p={p} lang={lang} />
                    ))}
                  </ul>
                  <div className="mt-3 border-t border-white/15 pt-2.5">
                    <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#d4aa5a]/90">
                      {UI[lang].show}
                    </span>
                    <p className="mt-1 text-[11px] leading-relaxed text-white/75">
                      {camp.show.map((s) => tr(s, lang)).join(" · ")}
                    </p>
                    <NightActivities camp={camp} lang={lang} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <NightFooter lang={lang} />
      </div>
    </div>
  );
}

// ── Concept 7 · Night, premium ticket / boarding-pass ───────────────────────
function NightTicketCard({ lang }: { lang: Lang }) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-[20px] shadow-[0_30px_80px_rgba(8,10,30,0.55)]"
      style={{ background: NIGHT_BG }}
    >
      <Stars />
      <div className="relative px-7 py-9 sm:px-10 sm:py-11">
        <NightHeader lang={lang} />

        <div className="mt-8 space-y-5">
          {CAMPS.map((camp) => (
            <div
              key={camp.name}
              className="relative flex overflow-hidden rounded-[16px] bg-white/[0.04] ring-1 ring-white/10"
            >
              <div className="relative w-[36%] min-w-[120px] shrink-0 overflow-hidden">
                <Img
                  src={camp.image}
                  alt={camp.name}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{ background: "linear-gradient(180deg, rgba(7,6,15,0.2) 0%, rgba(7,6,15,0.85) 100%)" }}
                />
                <div className="absolute inset-x-0 bottom-0 p-3.5">
                  <span
                    className="inline-block rounded-full bg-[#d4aa5a]/20 px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.2em] text-[#d4aa5a]"
                    style={{ fontFamily: "var(--font-geist)" }}
                  >
                    Agafay
                  </span>
                  <h3
                    className="mt-1 text-[18px] font-bold uppercase leading-none tracking-[0.02em] text-[#f4ecd8]"
                    style={{ fontFamily: "var(--font-geist)" }}
                  >
                    {camp.name}
                  </h3>
                </div>
              </div>

              <div className="relative w-0 shrink-0">
                <div className="absolute inset-y-3 left-0 border-l-2 border-dashed border-white/20" />
                <span
                  className="absolute -left-2 -top-2 h-4 w-4 rounded-full"
                  style={{ background: "#100d2a" }}
                  aria-hidden="true"
                />
                <span
                  className="absolute -bottom-2 -left-2 h-4 w-4 rounded-full"
                  style={{ background: "#0a0817" }}
                  aria-hidden="true"
                />
              </div>

              <div className="min-w-0 flex-1 p-5 ps-6">
                <ul className="grid grid-cols-1 gap-x-5 gap-y-2.5 sm:grid-cols-2">
                  {camp.packages.map((p) => (
                    <NightPriceRow key={p.name.en} p={p} lang={lang} />
                  ))}
                </ul>
                <NightExtras camp={camp} lang={lang} />
              </div>
            </div>
          ))}
        </div>

        <NightFooter lang={lang} />
      </div>
    </div>
  );
}

function Stars() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage: `
          radial-gradient(circle at 12% 18%, rgba(255,255,255,0.7) 0.6px, transparent 1.6px),
          radial-gradient(circle at 78% 12%, rgba(255,255,255,0.6) 0.6px, transparent 1.6px),
          radial-gradient(circle at 30% 8%, rgba(255,255,255,0.5) 0.6px, transparent 1.6px),
          radial-gradient(circle at 62% 22%, rgba(255,255,255,0.8) 0.6px, transparent 1.6px),
          radial-gradient(circle at 90% 30%, rgba(255,255,255,0.5) 0.6px, transparent 1.6px),
          radial-gradient(circle at 8% 36%, rgba(255,255,255,0.6) 0.6px, transparent 1.6px),
          radial-gradient(circle at 46% 15%, rgba(255,255,255,0.55) 0.6px, transparent 1.6px)`,
      }}
    />
  );
}

// ── Concept 3 · Comparison card ─────────────────────────────────────────────
function CompareCard({ lang }: { lang: Lang }) {
  const t = UI[lang];
  return (
    <div className="relative w-full overflow-hidden rounded-[20px] bg-[#fbf8f1] shadow-[0_24px_60px_rgba(90,60,30,0.22)] ring-1 ring-[#e3d8c2]">
      <div className="bg-[#1a1130] px-7 py-6 text-center">
        <span
          className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#d4aa5a]"
          style={{ fontFamily: "var(--font-geist)" }}
        >
          MaJourneys · Agafay
        </span>
        <h2
          className="mt-1 text-[26px] font-bold leading-tight text-white sm:text-[30px]"
          style={{ fontFamily: "var(--font-geist)" }}
        >
          {t.compareTitle}
        </h2>
        <p className="mt-1 text-[12px] font-bold uppercase tracking-[0.2em] text-[#d4aa5a]/90">
          {t.sub}
        </p>
        <p className="mt-1 text-[12px] text-white/60">{t.compareSub}</p>
      </div>

      <div className="grid grid-cols-[1.3fr_1fr_1fr] border-b border-[#e3d8c2]">
        <div className="px-5 py-3" />
        {CAMPS.map((camp) => (
          <div key={camp.name} className="flex flex-col items-center gap-2 px-3 py-3 text-center">
            <span className="h-14 w-full max-w-[120px] overflow-hidden rounded-[10px] shadow-sm" aria-hidden="true">
              <Img src={camp.image} alt="" className="h-full w-full object-cover" />
            </span>
            <span
              className="text-[14px] font-bold leading-tight sm:text-[15px]"
              style={{ fontFamily: "var(--font-geist)", color: camp.accent }}
            >
              {camp.name}
            </span>
          </div>
        ))}
      </div>

      {CAMPS[0].packages.map((rowPkg, i) => {
        const a = CAMPS[0].packages[i];
        const b = CAMPS[1].packages[i];
        const prices = [a?.price, b?.price].filter((n): n is number => typeof n === "number");
        const min = Math.min(...prices);
        return (
          <div
            key={rowPkg.name.en}
            className={`grid grid-cols-[1.3fr_1fr_1fr] items-stretch ${i % 2 ? "bg-white/60" : "bg-transparent"}`}
          >
            <div className="flex items-center px-5 py-3.5">
              <span className="text-[13px] font-bold leading-tight text-[#3a2a18] sm:text-[14px]">
                {tr(rowPkg.name, lang)}
              </span>
            </div>
            {[a, b].map((cell, idx) => {
              const cheapest = cell?.price === min && prices.length > 1;
              return (
                <div
                  key={idx}
                  className="flex flex-col items-center justify-center border-l border-[#e3d8c2] px-2 py-3 text-center"
                >
                  {cell ? (
                    <>
                      <span
                        className={`inline-flex items-center gap-1 text-[15px] font-bold tabular-nums sm:text-[16px] ${
                          cheapest ? "text-[#1f7a4d]" : "text-[#3a2a18]"
                        }`}
                        style={{ fontFamily: "var(--font-geist)" }}
                      >
                        {cell.price} MAD
                        {cheapest ? <Icon name="ri-arrow-down-line" className="text-[12px]" aria-hidden="true" /> : null}
                      </span>
                      <span className="mt-0.5 text-[10px] uppercase tracking-[0.08em]" style={{ color: "#9a8568" }}>
                        {tr(cell.time, lang)}
                      </span>
                    </>
                  ) : (
                    <span className="text-[13px] text-[#c4b89a]">—</span>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Activities (add-ons) */}
      <div className="grid grid-cols-[1.3fr_1fr_1fr] border-t border-[#e3d8c2] bg-[#1a1130]/[0.04]">
        <div className="flex items-center px-5 py-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#9a8568]">
            {t.activities}
          </span>
        </div>
        <div className="border-l border-[#e3d8c2]" />
        <div className="border-l border-[#e3d8c2]" />
      </div>
      {CAMPS[0].activities.map((rowAct, i) => {
        const a = CAMPS[0].activities[i];
        const b = CAMPS[1].activities[i];
        const prices = [a?.price, b?.price].filter((n): n is number => typeof n === "number");
        const min = Math.min(...prices);
        return (
          <div
            key={rowAct.name.en}
            className={`grid grid-cols-[1.3fr_1fr_1fr] items-stretch ${i % 2 ? "bg-white/60" : "bg-transparent"}`}
          >
            <div className="flex items-center px-5 py-3">
              <span className="text-[13px] font-bold leading-tight text-[#3a2a18] sm:text-[14px]">
                {tr(rowAct.name, lang)}
              </span>
            </div>
            {[a, b].map((cell, idx) => {
              const cheapest = cell?.price === min && prices.length > 1;
              return (
                <div
                  key={idx}
                  className="flex flex-col items-center justify-center border-l border-[#e3d8c2] px-2 py-3 text-center"
                >
                  {cell ? (
                    <>
                      <span
                        className={`inline-flex items-center gap-1 text-[15px] font-bold tabular-nums sm:text-[16px] ${
                          cheapest ? "text-[#1f7a4d]" : "text-[#3a2a18]"
                        }`}
                        style={{ fontFamily: "var(--font-geist)" }}
                      >
                        {cell.price} MAD
                        {cheapest ? <Icon name="ri-arrow-down-line" className="text-[12px]" aria-hidden="true" /> : null}
                      </span>
                      <span className="mt-0.5 text-[10px] uppercase tracking-[0.08em]" style={{ color: "#9a8568" }}>
                        {cell.duration}
                      </span>
                    </>
                  ) : (
                    <span className="text-[13px] text-[#c4b89a]">—</span>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      <div className="grid grid-cols-[1.3fr_1fr_1fr] border-t border-[#e3d8c2] bg-[#f3ecdc]">
        <div className="flex items-center px-5 py-3">
          <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#9a8568]">
            {t.show}
          </span>
        </div>
        {CAMPS.map((camp) => (
          <div
            key={camp.name}
            className="border-l border-[#e3d8c2] px-3 py-3 text-center text-[11px] leading-relaxed text-[#5a4a30]"
          >
            {camp.show.map((s) => tr(s, lang)).join(", ")}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 border-t border-[#e3d8c2] px-5 py-4 text-[12px] font-medium text-[#5a4a30]">
        <span className="inline-flex items-center gap-1.5">
          <Icon name="ri-car-line" className="text-[14px] text-[#9a8568]" aria-hidden="true" />
          {t.transport}
        </span>
        <span className="inline-flex items-center gap-1.5 font-bold text-[#1f7a4d]">
          <Icon name="ri-whatsapp-line" className="text-[15px]" aria-hidden="true" />
          {t.book}
        </span>
      </div>
    </div>
  );
}
